<script setup>
import { computed } from "vue";
import { track } from "jiffy-analytics";

const props = defineProps({
  compositions: {
    type: Array,
    required: true,
    default: () => []
  },
  selectedColor: {
    type: Object,
    default: () => null
  },
  isLoading: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["on-color-select"]);

const colorsLoading = computed(() => props.isLoading && props.compositions.length === 0);

const getItemClasses = composition => ({
  "color-picker__item--selected": props.selectedColor?.id === composition.id,
  "color-picker__item--loading": props.isLoading,
  "color-picker__item--with-border": composition.colorName.includes("White")
});

const handleColorClick = color => {
  if (props.isLoading) return;
  track("cart_add_color_select", {
    color: color.colorName
  });
  emit("on-color-select", color);
};
</script>

<template>
  <div class="color-picker" :class="{ 'color-picker--skeleton-item': colorsLoading }">
    <div class="color-picker__title">Choose your color</div>
    <div class="color-picker__items">
      <div
        v-for="composition in compositions"
        :key="composition.id"
        class="color-picker__item"
        :class="getItemClasses(composition)"
        :title="composition.colorName"
        :style="{ backgroundColor: composition.colorHex }"
        @click="handleColorClick(composition)"
      ></div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.color-picker {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 20px 0;
  gap: 10px;

  &--skeleton-item {
    @include skeleton(100%);

    border: none;
    pointer-events: none;
    width: 100%;
    height: 76px;

    &::before {
      border-radius: 4px;
      background-image: linear-gradient(
        90deg,
        $hercules-loading 0,
        $gray-border-lightest 80px,
        $hercules-loading 200px
      );
    }
  }

  &__title {
    font-family: Arial;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: $text-black;
  }

  &__items {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
  }

  &__item {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    cursor: pointer;

    &--selected {
      outline: 1px solid rgba($black, 0.25);
      outline-offset: 1px;
    }

    &--loading {
      opacity: 0.5;
      pointer-events: none;
    }

    &--with-border {
      outline: 1px solid rgba($black, 0.25);
    }
  }
}
</style>
